import React, { useState, useEffect } from "react";
import FiltroPropiedades from "./components/FiltroPropiedades";
import PropertyGrid from "./components/PropertyGrid";
import PropertyDetail from "./components/PropertyDetail";
import { apiClient } from "../../lib/api";
import PropertyActionModal from "./components/PropertyActionModal";
import { Home } from "lucide-react";
// import { formatDate } from "../../lib/utils"; 

// ----------------------------------------------------------------------
// 1. Interfaces
// ----------------------------------------------------------------------
export interface Property {
    id: number;
    name: string;
    location: string;
    price: number;
    currency: string;
    bookings: number;
    rating: number | null;
    reviews?: number;
    status: "AC" | "PE" | "SU";
    statusLabel: string;
    type: string;
    icon: string;
    gradient: string;
    host?: string;
    mainPhotoUrl?: string | null;
    capacity?: string;
    rooms?: string;
    area?: string;
    occupancyRate?: number;
    totalIncome?: number;
    jomCommission?: number;
    amenities?: string[];
    banos?: {
        privados: any;
        privadosAlojamiento: any;
        compartidos: any;
        total: any;
    };
}

export interface ApiResponse {
    pagination: {
        totalItemCount: number;
        totalPageCount: number;
        pageSize: number;
        currentPage: number;
        hasPrevious: boolean;
        hasNext: boolean;
    };
    data: any[];
}

export interface PropertyStats {
    totalProperties: number;
    totalActiveProperties: number;
    pending: number;
    suspended: number;
}

// ======================================================================
// COMPONENTE: PÁGINA PRINCIPAL (CONTENEDOR)
// ======================================================================
const PropiedadesPageNew = () => {
    // ESTADOS GLOBALES
    const [activeFilter, setActiveFilter] = useState("todas");
    const [activeView, setActiveView] = useState("grid");
    //const [sortOrder, setSortOrder] = useState("recent");
    const [propiedades, setPropiedades] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<ApiResponse["pagination"] | null>(null);
    const [stats, setStats] = useState<PropertyStats | null>(null);

    // ESTADOS MODALES Y DRAWER
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isDrawerLoading, setIsDrawerLoading] = useState(false);
    const [selectedProp, setSelectedProp] = useState<Property | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [actionType, setActionType] = useState<"status" | "delete" | "pending">("status");
    const [toast, setToast] = useState<{ msg: string; type: "success" | "danger" | "info" | "gold" } | null>(null);

    // CONFIGURACIÓN DE FILTROS
    const filters = [
        { id: "todas", label: "Todas", queryStatus: "" },
        { id: "activas", label: "Activas", queryStatus: "AC" },
        { id: "pendientes", label: "Pendientes", queryStatus: "PE" },
        { id: "suspendidas", label: "Suspendidas", queryStatus: "SU" },
    ];
    //const currentFilter = filters.find((f) => f.id === activeFilter);

    // LLAMADAS API
    const fetchStats = async () => {
        try {
            //setStats({ totalProperties: 312, active: 280, pending: 20, suspended: 12 });
            const response = await apiClient.get<any>('GetTotalPublishedProperties');
            if (response?.data) {
                setStats(response.data);
            }
            console.log('stats:', response.data);
        } catch (error) { console.error("Error cargando estadísticas de propiedades:", error); }
    };

    const fetchProperties = async () => {
        setLoading(true);
        try {
            // Parámetros
            const params = new URLSearchParams({
                pageNumber: currentPage.toString(),
                pageSize: '15',
                statusFilter: activeFilter
            });

            if (searchTerm.trim()) {
                params.append('search', searchTerm.trim());
            }

            // Llamada al servicio
            const endpoint = `GetPropertiesList?${params.toString()}`;
            const response = await apiClient.get<any>(endpoint);

            if (response.hasError) {
                throw new Error(response.resultError || "Error en la API");
            }

            const propertiesList = response.data?.properties || [];

            const mappedProperties: Property[] = propertiesList.map((p: any) => {
                let statusCode: "AC" | "PE" | "SU" = "PE";
                if (p.estado === "Activa") statusCode = "AC";
                if (p.estado === "Suspendida") statusCode = "SU";

                return {
                    id: p.id,
                    name: p.nombre || "Sin nombre",
                    location: p.ciudad || "Ubicación desconocida",
                    price: p.precioPorNoche || '-',
                    currency: p.moneda || "BOB",
                    bookings: p.reservas || 0,
                    rating: p.rating,
                    status: statusCode,
                    statusLabel: p.estado || "Pendiente",
                    type: p.tipoPropiedad || "Propiedad",
                    host: p.hostNombre || "Anfitrión desconocido",
                    mainPhotoUrl: p.mainPhotoUrl,
                    icon: statusCode === 'AC' ? '🏢' : statusCode === 'PE' ? '🏚' : '🏗',
                    gradient: statusCode === 'AC' ? 'from-blue-50 to-blue-100' : statusCode === 'PE' ? 'from-orange-50 to-orange-100' : 'from-gray-100 to-gray-200',
                };
            });

            /* mappedProperties.sort((a, b) => {
                if (sortOrder === "highestPrice") return b.price - a.price;
                if (sortOrder === "mostBookings") return b.bookings - a.bookings;
                if (sortOrder === "highestRating") return (b.rating || 0) - (a.rating || 0);
                return a.id - b.id; // "recent"
            }); */

            setPropiedades(mappedProperties);
            setPagination(response.pagination);

        } catch (error) {
            console.error("Error obteniendo propiedades:", error);
            setPropiedades([]);
        } finally {
            setLoading(false);
        }
    };

    //useEffect(() => { fetchProperties(); }, [activeFilter, currentPage, searchTerm, sortOrder]);
    useEffect(() => { fetchProperties(); }, [activeFilter, currentPage, searchTerm]);
    useEffect(() => { fetchStats(); }, []);

    // HANDLERS GLOBALES
    const handleFilterChange = (filterId: string) => {
        setActiveFilter(filterId);
        setCurrentPage(1);
        setIsDrawerOpen(false);
    };

    const openActionModal = (prop: Property, type: "status" | "delete" | "pending" = "status", e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setSelectedProp(prop);
        setActionType(type);
        setIsModalOpen(true);
        setIsDrawerOpen(false);
    };

    const openDrawer = async (prop: Property) => {
        setSelectedProp(prop);
        setIsDrawerOpen(true);
        if (!prop.id && prop.id !== 0) return;

        setIsDrawerLoading(true);
        try {
            // Servicio de detalle
            const response = await apiClient.get<any>(`GetPropertyDetail/${prop.id}`);
            const details = response.data;

            if (details) {
                setSelectedProp((prev) => {
                    if (!prev) return null;

                    const tasaString = details.metricas?.tasaOcupacion || "0%";
                    const tasaNumero = parseInt(tasaString.replace('%', '')) || 0;

                    return {
                        ...prev,
                        host: details.anfitrion || prev.host,
                        capacity: details.capacidad ? `${details.capacidad} personas` : "No especificado",
                        rooms: details.habitaciones ?? 0,
                        banos: details.banos,
                        area: details.superficieM2 ? `${details.superficieM2} m²` : "No especificado",
                        occupancyRate: tasaNumero,
                        totalIncome: details.metricas?.ingresosGenerados ?? 0,
                        jomCommission: details.metricas?.comision ?? 0,
                        amenities: details.amenidades || [],
                        bookings: details.metricas?.reservasTotales || '-',
                        rating: details.metricas?.rating,
                        reviews: details.metricas?.resenas,
                        price: details.precioPorNoche ?? prev.price,
                        currency: details.moneda || prev.currency || "BOB",
                    };
                });
            }
        } catch (error) {
            showToast("No se pudo cargar el detalle de la propiedad", "danger");
        } finally {
            setIsDrawerLoading(false);
        }
    };

    const showToast = (msg: string, type: "success" | "danger" | "info" | "gold" = "info") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 5000);
    };

    const confirmAction = async () => {
        if (!selectedProp || isUpdating) return;
        setIsUpdating(true);

        try {
            if (actionType === "status") {
                //const isCurrentlyActive = selectedProp.status === "AC";
                const isSuspending = selectedProp.status !== "SU";

                // Elige dinámicamente a Blocked o Unblocked
                const actionPath = isSuspending ? "Blocked" : "Unblocked";
                const endpoint = `ListingRent/${actionPath}?listingRentId=${selectedProp.id}`;

                const response = await apiClient.put<any>(endpoint, {});

                if (response.hasError) {
                    throw new Error(response.resultError || "Error al actualizar el estado de la propiedad");
                }

                showToast(
                    isSuspending ? "Propiedad suspendida exitosamente" : "Propiedad reactivada exitosamente",
                    isSuspending ? "danger" : "success"
                );

            } else if (actionType === "pending") {
                // Servicio para marcar como pendiente
                await new Promise(resolve => setTimeout(resolve, 800)); // temporal
                showToast("Propiedad marcada como pendiente", "gold");
            } else {
                showToast("Propiedad eliminada", "info");
            }

            setIsModalOpen(false);
            setIsDrawerOpen(false);
            fetchProperties();
            fetchStats();

        } catch (error: any) {
            console.error("Error al procesar la acción:", error);
            showToast(error.message || "Error al comunicar con el servidor.", "danger");
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="flex w-full h-screen bg-gray-50 text-gray-900 font-sans overflow-hidden">
            <div className="flex-1 flex flex-col overflow-hidden">

                {/* TOPBAR */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 md:px-[32px] py-4 md:py-[20px] border-b border-gray-200 bg-white sticky top-0 z-10 gap-4 sm:gap-0">
                    <div>
                        <h1 className="text-[20px] font-bold flex items-center gap-[10px] m-0 text-gray-900">
                            {/* <svg width="22" height="22" fill="none" stroke="#4f6df5" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                            </svg> */}
                            <Home size={22} color="#4f6df5" strokeWidth={2} />
                            Propiedades
                        </h1>
                        <p className="text-xs text-gray-500 mt-1 m-0">
                            {stats ? `${stats.totalActiveProperties} propiedades activas en la plataforma` : "Cargando..."}
                        </p>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <button className="flex text-xs font-semibold px-3.5 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-50 transition-colors shadow-sm">
                            Exportar
                        </button>
                        {/* <button className="text-xs font-semibold px-4 py-2 rounded-lg bg-[#4f6df5] text-white flex items-center gap-1.5 hover:bg-[#3b5bdb] transition-colors shadow-sm">
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            Nueva propiedad
                        </button> */}
                    </div>
                </div>

                {/* COMPONENTE: Filtro */}
                <FiltroPropiedades
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    filters={filters}
                    activeFilter={activeFilter}
                    onFilterChange={handleFilterChange}
                    //sortOrder={sortOrder}
                    //onSortChange={(val: string) => { setSortOrder(val); setCurrentPage(1); }}
                    activeView={activeView}
                    onViewChange={setActiveView}
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-7 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    {/* COMPONENTE: Grid (Mosaico) y Cards */}
                    <PropertyGrid
                        propiedades={propiedades}
                        loading={loading}
                        activeView={activeView}
                        selectedProp={selectedProp}
                        isDrawerOpen={isDrawerOpen}
                        onPropClick={openDrawer}
                    />

                    {/* PAGINATION CONTROLS */}
                    {pagination && pagination.totalPageCount > 1 && (
                        <div className="flex justify-center items-center gap-3 mt-8 pt-5 border-t border-gray-200">
                            <button
                                disabled={!pagination.hasPrevious}
                                onClick={() => setCurrentPage((p) => p - 1)}
                                className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                            >
                                Anterior
                            </button>
                            <span className="text-xs text-gray-500 font-medium">
                                Página {pagination.currentPage} de {pagination.totalPageCount}
                            </span>
                            <button
                                disabled={!pagination.hasNext}
                                onClick={() => setCurrentPage((p) => p + 1)}
                                className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                            >
                                Siguiente
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* COMPONENTE: Detalle */}
            <PropertyDetail
                selectedProp={selectedProp}
                isDrawerOpen={isDrawerOpen}
                isDrawerLoading={isDrawerLoading}
                onClose={() => setIsDrawerOpen(false)}
                onActionClick={openActionModal}
            />

            {/* COMPONENTE MODAL DE ACCIONES */}
            <PropertyActionModal
                isOpen={isModalOpen}
                selectedProp={selectedProp}
                actionType={actionType}
                isUpdating={isUpdating}
                onClose={() => setIsModalOpen(false)}
                onConfirm={confirmAction}
            />

            {/* TOAST ESTILOS */}
            <style>{`
                @keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            {toast && (
                <div className="fixed bottom-6 right-6 z-[200] bg-white rounded-[10px] text-[13px] font-semibold py-3 px-5 shadow-lg border"
                    style={{
                        animation: "toastIn 0.3s ease forwards",
                        borderColor: toast.type === "success" ? "#34d399" : toast.type === "danger" ? "#f87171" : toast.type === "gold" ? "#f59e0b" : "#e5e7eb",
                        color: toast.type === "success" ? "#059669" : toast.type === "danger" ? "#dc2626" : toast.type === "gold" ? "#d97706" : "#1f2937"
                    }}>
                    {toast.msg}
                </div>
            )}
        </div>
    );
};

export default PropiedadesPageNew;